import _slicedToArray from "/builds/vistaprint-org/digital/acquisition/merchandising-components/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useMenuTrigger } from '@react-aria/menu';
import { useListState } from '@react-stately/list';
import { useMenuTriggerState } from '@react-stately/menu';
import React__default, { useRef, useMemo, useEffect } from 'react';
import { SwanListboxButtonContext } from './listbox-button.context.js';
import { useDisabledOptionState, useListboxList, SwanListboxListContext } from './listbox-list.context.js';
import { SwanPopoverContextProvider, useSwanPopover } from '../popover/popover.context.js';
import { useCollectionContext } from '../../contexts/internal/collection/collection.context.js';
import { useNonNullishContext } from '../../hooks/use-non-nullish-context.js';

function _define_property(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _object_spread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === "function") {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _define_property(target, key, source[key]);
    });
  }

  return target;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _object_spread_props(target, source) {
  source = source != null ? source : {};

  if (Object.getOwnPropertyDescriptors) {
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
  } else {
    ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _object_without_properties(source, excluded) {
  if (source == null) return {};

  var target = _object_without_properties_loose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _object_without_properties_loose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function SwanListboxContextProviderInner(props) {
  var children = props.children,
      disabled = props.disabled,
      selectionProps = props.selectionProps;

  var _useSwanPopover = useSwanPopover(),
      triggerRef = _useSwanPopover.triggerRef,
      triggerProps = _useSwanPopover.triggerProps,
      overlayState = _useSwanPopover.overlayState;

  var _useMenuTrigger = useMenuTrigger(_object_spread_props(_object_spread({}, triggerProps), {
    isDisabled: disabled,
    type: 'listbox'
  }), overlayState, triggerRef),
      menuTriggerProps = _useMenuTrigger.menuTriggerProps,
      menuProps = _useMenuTrigger.menuProps;

  var listboxButtonContextValue = useMemo(function () {
    return {
      triggerProps: menuTriggerProps,
      triggerDisabled: disabled !== null && disabled !== void 0 ? disabled : false
    };
  }, [menuTriggerProps, disabled]);

  var _useDisabledOptionSta = useDisabledOptionState(props.selectionProps),
      _useDisabledOptionSta2 = _slicedToArray(_useDisabledOptionSta, 2),
      disabledKeys = _useDisabledOptionSta2[0],
      setDisabledKeys = _useDisabledOptionSta2[1]; // tweak the selection props to remove disabled keys and adjust the onSelectionChange behavior


  var listProps = useMemo(function () {
    var _ref = selectionProps !== null && selectionProps !== void 0 ? selectionProps : {},
        _onSelectionChange = _ref.onSelectionChange,
        rest = _object_without_properties(_ref, ["onSelectionChange"]);

    return _object_spread_props(_object_spread({}, menuProps, rest), {
      disabledKeys: disabledKeys,
      onSelectionChange: function onSelectionChange(key) {
        if (_onSelectionChange != null) {
          _onSelectionChange(key);
        } // close the popover when a single option is selected


        if (rest.selectionMode !== 'multiple') {
          overlayState.close();
        }
      }
    });
  }, [disabledKeys, menuProps, overlayState, selectionProps]);
  var listboxRef = useRef(null);

  var _useCollectionContext = useCollectionContext(),
      childItems = _useCollectionContext.childItems;

  var listState = useListState(_object_spread_props(_object_spread({}, listProps), {
    children: childItems
  }));
  var listContextValue = useListboxList(listProps, listboxRef, listState, setDisabledKeys);
  return /*#__PURE__*/React__default.createElement(SwanListboxButtonContext.Provider, {
    value: listboxButtonContextValue
  }, /*#__PURE__*/React__default.createElement(SwanListboxListContext.Provider, {
    value: listContextValue
  }, children));
}

function SwanListboxContextProvider(props) {
  var menuTriggerState = useMenuTriggerState({});
  var popoverRef = useRef(null);
  var triggerRef = useRef(null);
  var popoverContext = useMemo(function () {
    return {
      popoverRef: popoverRef,
      triggerRef: triggerRef,
      overlayState: menuTriggerState,
      overlayProps: {},
      triggerProps: {},
      fullWidth: props.fullWidth
    };
  }, [popoverRef, triggerRef, menuTriggerState, props.fullWidth]);
  return /*#__PURE__*/React__default.createElement(SwanPopoverContextProvider, {
    value: popoverContext
  }, /*#__PURE__*/React__default.createElement(SwanListboxContextProviderInner, props));
}

var ListboxWithFloatingLabelContext = /*#__PURE__*/React__default.createContext(null);

var useListboxWithFloatingLabelContext = function useListboxWithFloatingLabelContext() {
  return React__default.useContext(ListboxWithFloatingLabelContext);
};

var useNonNullishListboxWithFloatingLabelContext = function useNonNullishListboxWithFloatingLabelContext() {
  return useNonNullishContext(ListboxWithFloatingLabelContext);
};

function useFloatingLabelProps(props) {
  var className = props.className; // communicate the id up to the ListboxWithFloatingLabel comp if necessary

  var withFloatingLabelContext = useListboxWithFloatingLabelContext();
  var hasFloatingLabel = withFloatingLabelContext !== undefined;

  if (!hasFloatingLabel) {
    return props;
  }

  return _object_spread_props(_object_spread({}, props), {
    className: className
  });
}

function useListboxFloatingLabelWithButton(buttonId) {
  var _useListboxWithFloatingLabelContext;

  var _ref2 = (_useListboxWithFloatingLabelContext = useListboxWithFloatingLabelContext()) !== null && _useListboxWithFloatingLabelContext !== void 0 ? _useListboxWithFloatingLabelContext : {},
      setButtonId = _ref2.setButtonId;

  useEffect(function () {
    if (setButtonId) {
      setButtonId(buttonId);
    }
  }, [buttonId, setButtonId]);
}

export { ListboxWithFloatingLabelContext, SwanListboxContextProvider, useFloatingLabelProps, useListboxFloatingLabelWithButton, useListboxWithFloatingLabelContext, useNonNullishListboxWithFloatingLabelContext };